import { type CoordinatorAutomationRead } from '@apps/web/components/automations/types';
import { type components } from '@libraries/typescript/api_client/generated_api_types';

type CreateRequest =
    components['schemas']['CoordinatorAutomationCreateRequest'];
type UpdateRequest =
    components['schemas']['CoordinatorAutomationUpdateRequest'];
type ScheduleKind = CoordinatorAutomationRead['schedule']['kind'];

export const DEFAULT_MAX_FIRES_PER_HOUR = 10;
export const DEFAULT_DAILY_FIRE_CAP = 100;

export type AutomationDialogForm = {
    readonly name: string;
    readonly prompt: string;
    readonly triggerType: 'schedule';
    readonly kind: ScheduleKind;
    readonly time: string;
    readonly weekday: number;
    readonly maxFiresPerHour: number;
    readonly dailyFireCap: number;
};

export const EMPTY_AUTOMATION_FORM: AutomationDialogForm = {
    name: '',
    prompt: '',
    triggerType: 'schedule',
    kind: 'weekly',
    time: '09:00',
    weekday: 0,
    maxFiresPerHour: DEFAULT_MAX_FIRES_PER_HOUR,
    dailyFireCap: DEFAULT_DAILY_FIRE_CAP,
};

export function automationFormFromRead(
    automation: CoordinatorAutomationRead,
): AutomationDialogForm {
    return {
        name: automation.name,
        prompt: automation.prompt,
        triggerType: 'schedule',
        kind: automation.schedule.kind,
        time: automation.schedule.time,
        weekday: automation.schedule.weekday ?? EMPTY_AUTOMATION_FORM.weekday,
        maxFiresPerHour: automation.max_fires_per_hour,
        dailyFireCap: automation.daily_fire_cap,
    };
}

export function canSaveAutomationForm(form: AutomationDialogForm): boolean {
    if (form.name.trim().length === 0 || form.prompt.trim().length === 0) {
        return false;
    }
    return true;
}

function automationRequestFields(form: AutomationDialogForm) {
    return {
        name: form.name.trim(),
        prompt: form.prompt.trim(),
        max_fires_per_hour: form.maxFiresPerHour,
        daily_fire_cap: form.dailyFireCap,
        trigger_type: 'schedule' as const,
        schedule: {
            kind: form.kind,
            time: form.time,
            weekday: form.kind === 'weekly' ? form.weekday : null,
        },
    };
}

export function automationCreateBody(
    form: AutomationDialogForm,
): CreateRequest {
    return { ...automationRequestFields(form), enabled: true };
}

/** Leaves `enabled` out so saving an edit cannot resume a paused automation. */
export function automationUpdateBody(
    form: AutomationDialogForm,
): UpdateRequest {
    return automationRequestFields(form);
}
